// Mirrors internal/kinds/git: optimistic status change + queued write-back.
import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import type { GitIssueSpec, GitIssueStatus, PullRequestSpec, PullRequestStatus, WriteBack } from './schema'

export type GitIssueResource = Resource<GitIssueSpec, GitIssueStatus>
export type PullRequestResource = Resource<PullRequestSpec, PullRequestStatus>

const asObj = (a: unknown): Record<string, unknown> => (a && typeof a === 'object' ? (a as Record<string, unknown>) : {})
const bodyOf = (args: unknown, what: string) => {
  const b = asObj(args).body
  if (typeof b !== 'string' || !b) throw invalid(`${what}: body is required`)
  return b
}
const queued = (list: WriteBack[] | undefined, action: string, args: unknown, at: string): WriteBack[] => {
  const a = asObj(args)
  const wb: WriteBack = { action, at }
  if (Object.keys(a).length) wb.args = a
  return [...(list ?? []), wb]
}

export const issueDefaults = (spec: Partial<GitIssueSpec>): GitIssueSpec =>
  ({ body: '', author: '', labels: [], assignees: [], milestone: '', ...spec }) as GitIssueSpec
export const issueInitialStatus = (r: GitIssueResource): GitIssueStatus => ({ state: r.spec.state, last_touched: formatTime(new Date(r.created_at)) })
export const issueDisplayName = (r: GitIssueResource) => `${r.spec.repo}#${r.spec.number} ${r.spec.title}`

export const applyIssue = (r: GitIssueResource, action: string, args: unknown, at: string): GitIssueResource => {
  const when = formatTime(new Date(at))
  const st: GitIssueStatus = { ...r.status }
  switch (action) {
    case 'comment':
      bodyOf(args, 'comment')
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'close':
      if (st.state === 'closed') throw conflict('issue is already closed')
      st.state = 'closed'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'reopen':
      if (st.state === 'open') throw conflict('issue is already open')
      st.state = 'open'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'link_task': {
      const id = asObj(args).task_id
      if (typeof id !== 'string' || !id) throw invalid('link_task: task_id is required')
      st.task_id = id
      break
    }
    default:
      throw invalid(`unknown action ${action}`)
  }
  st.last_touched = when
  return { ...r, status: compact(st) }
}

export const pullDefaults = (spec: Partial<PullRequestSpec>): PullRequestSpec =>
  ({ body: '', author: '', draft: false, labels: [], requested_reviewers: [], review_requested: false, mergeable: null, checks: 'unknown', head: '', base: '', ...spec }) as PullRequestSpec
export const pullInitialStatus = (r: PullRequestResource): PullRequestStatus => ({ state: r.spec.state, last_touched: formatTime(new Date(r.created_at)) })
export const pullDisplayName = (r: PullRequestResource) => `${r.spec.repo}#${r.spec.number} ${r.spec.title}`

export const applyPull = (r: PullRequestResource, action: string, args: unknown, at: string): PullRequestResource => {
  const when = formatTime(new Date(at))
  const st: PullRequestStatus = { ...r.status }
  const openOnly = () => {
    if (st.state !== 'open') throw conflict(`pull request is ${st.state}`)
  }
  switch (action) {
    case 'approve':
      openOnly()
      st.review_state = 'approved'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'request_changes':
      openOnly()
      bodyOf(args, 'request_changes')
      st.review_state = 'changes_requested'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'comment':
      bodyOf(args, 'comment')
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'merge':
      openOnly()
      st.state = 'merged'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'close':
      openOnly()
      st.state = 'closed'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    case 'reopen':
      if (st.state !== 'closed') throw conflict(`pull request is ${st.state}`)
      st.state = 'open'
      st.writebacks = queued(st.writebacks, action, args, when)
      break
    default:
      throw invalid(`unknown action ${action}`)
  }
  st.last_touched = when
  return { ...r, status: compact(st) }
}
